/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { Placement } from '@coursera-web/cds-labs';
import { TooltipWrapper } from '@coursera-web/cds-labs';

import constants from 'pages/open-course/common/constants';

import type { IconProps } from '@coursera/cds-core';
import {
  AIGenerateIcon,
  AssignmentCircledIcon,
  DialogueCircledIcon,
  DiscussionPromptCircledIcon,
  LTICircledIcon,
  LockCircledIcon,
  ProgrammingAssignmentCircledIcon,
  QuizCircledIcon,
  ReadingCircledIcon,
  ReviewCircledIcon,
  StaffGradedCircledIcon,
  VideoCircledIcon,
  WarningIcon,
  WebCircledIcon,
} from '@coursera/cds-icons';
import {
  SvgAssignment,
  SvgBrowser,
  SvgCircleWarningOutline,
  SvgDiscussionForum,
  SvgLTI,
  SvgLockFilled,
  SvgProgrammingAssignment,
  SvgQuiz,
  SvgReading,
  SvgReviewYourPeers,
  SvgVideo,
} from '@coursera/coursera-ui/svg';

import SvgDialogueIcon from 'bundles/coach-item/common/components/DialogueIcon';
import type { ItemType } from 'bundles/item/types/ItemType';

import _t from 'i18n!nls/item';

const {
  items: { assessmentTypes, gradableTypes },
} = constants;

const cuiIconMap = {
  closedPeer: SvgReviewYourPeers,
  discussionPrompt: SvgDiscussionForum,
  gradedDiscussionPrompt: SvgDiscussionForum,
  exam: SvgQuiz,
  gradedLti: SvgLTI,
  gradedPeer: SvgReviewYourPeers,
  gradedProgramming: SvgProgrammingAssignment,
  lecture: SvgVideo,
  notebook: SvgProgrammingAssignment,
  lock: SvgLockFilled,
  peer: SvgReviewYourPeers,
  phasedPeer: SvgAssignment,
  programming: SvgProgrammingAssignment,
  quiz: SvgQuiz,
  review: SvgReading,
  splitPeerReviewItem: SvgReviewYourPeers,
  staffGraded: SvgAssignment,
  ungradedAssignment: SvgAssignment,
  supplement: SvgReading,
  teammateReview: SvgReviewYourPeers,
  ungradedLti: SvgLTI,
  ungradedWidget: SvgProgrammingAssignment,
  coach: SvgDialogueIcon,
  rolePlay: SvgDialogueIcon,
  gradedCoach: SvgDialogueIcon,
  gradedWidget: SvgProgrammingAssignment,
  dialogue: SvgDialogueIcon,
  ungradedProgramming: SvgProgrammingAssignment,
  workspaceLauncher: SvgBrowser,
  ungradedLab: SvgBrowser,
  gradedLab: SvgBrowser,
  wiseFlow: SvgLTI,
  gradedPlaceholder: SvgCircleWarningOutline,
  ungradedPlaceholder: SvgCircleWarningOutline,
  placeholder: SvgCircleWarningOutline,
  // currently pseudo-items for the learning experience
  singleQuestionSubmit: undefined,
  assessOpenSinglePage: undefined,
  lessonChoice: undefined,
  assignmentGroup: undefined,
  injectable: undefined,
  'ai-generated': undefined,
};

const cdsIconMap = {
  closedPeer: ReviewCircledIcon,
  discussionPrompt: DiscussionPromptCircledIcon,
  gradedDiscussionPrompt: DiscussionPromptCircledIcon,
  exam: QuizCircledIcon,
  gradedLti: LTICircledIcon,
  gradedPeer: ReviewCircledIcon,
  gradedProgramming: ProgrammingAssignmentCircledIcon,
  lecture: VideoCircledIcon,
  notebook: ProgrammingAssignmentCircledIcon,
  lock: LockCircledIcon,
  peer: ReviewCircledIcon,
  phasedPeer: StaffGradedCircledIcon,
  programming: ProgrammingAssignmentCircledIcon,
  quiz: QuizCircledIcon,
  review: ReadingCircledIcon,
  splitPeerReviewItem: ReviewCircledIcon,
  staffGraded: AssignmentCircledIcon,
  ungradedAssignment: AssignmentCircledIcon,
  supplement: ReadingCircledIcon,
  teammateReview: ReviewCircledIcon,
  ungradedLti: LTICircledIcon,
  ungradedWidget: ProgrammingAssignmentCircledIcon,
  coach: DialogueCircledIcon,
  gradedCoach: DialogueCircledIcon,
  gradedWidget: ProgrammingAssignmentCircledIcon,
  dialogue: DialogueCircledIcon,
  rolePlay: DialogueCircledIcon,
  ungradedProgramming: ProgrammingAssignmentCircledIcon,
  workspaceLauncher: WebCircledIcon,
  ungradedLab: WebCircledIcon,
  gradedLab: WebCircledIcon,
  wiseFlow: LTICircledIcon,
  gradedPlaceholder: WarningIcon,
  ungradedPlaceholder: WarningIcon,
  placeholder: WarningIcon,
  // currently pseudo-items for the learning experience
  singleQuestionSubmit: undefined,
  assessOpenSinglePage: undefined,
  lessonChoice: undefined,
  assignmentGroup: undefined,
  injectable: undefined,
  'ai-generated': AIGenerateIcon,
};

const wrapperHeightToCdsIconSizeMap = {
  small: 16,
  medium: 20,
  large: 24,
  xlarge: 36,
};

export type Props = {
  type: ItemType;
  size: number;
  title?: string;
  style?: React.CSSProperties;
  color?: string;
  hasTooltip?: boolean;
  ariaHidden?: boolean;
  tooltipPlacement?: Placement;
  useCdsIcon?: boolean;
  cdsIconProps?: Omit<IconProps, 'title' | 'style' | 'aria-hidden'>;
};

const ItemIcon: React.FC<Props> = ({ size = 16, hasTooltip = false, ariaHidden = false, ...props }) => {
  const renderIcon = (hasTooltipText: boolean) => {
    const { type, style, title, color, useCdsIcon, cdsIconProps } = props;
    const { size: cdsSize = 'large', color: cdsColor } = cdsIconProps || {};
    const CuiIconComponent = !useCdsIcon && cuiIconMap[type];
    const CdsIconComponent = useCdsIcon && cdsIconMap[type];

    if (!CuiIconComponent && !CdsIconComponent) {
      return <span />;
    }

    // Override CDS size with CUI size if a non-standard icon size is passed in
    const useCuiSizeForCdsIcon = size && !Object.values(wrapperHeightToCdsIconSizeMap).includes(size);

    const styles = {
      wrapper: css({
        ...(useCdsIcon && {
          /* This makes the wrapper the same height as the Icon to help get rid of unbalanced sizing for the CDS Icons
             in some existing components e.g. ItemRowV2 */
          height: `${useCuiSizeForCdsIcon ? size : wrapperHeightToCdsIconSizeMap[cdsSize]}px`,

          ...(useCuiSizeForCdsIcon && {
            svg: {
              width: `${size}px`,
              height: `${size}px`,
            },
          }),
        }),
      }),
    };

    return (
      <div className="rc-ItemIcon horizontal-box" css={styles.wrapper}>
        {CuiIconComponent && (
          <CuiIconComponent
            size={size}
            style={style}
            suppressTitle={hasTooltipText}
            title={title}
            color={color}
            aria-hidden={ariaHidden}
          />
        )}
        {CdsIconComponent && (
          <CdsIconComponent
            size={cdsSize}
            style={style}
            title={hasTooltipText ? '' : title}
            color={cdsColor}
            aria-hidden={ariaHidden}
            data-testid={`item-icon-${type}`}
          />
        )}
      </div>
    );
  };

  const { type, tooltipPlacement } = props;

  const isAssessment = assessmentTypes.includes(type);
  const isGradedAssessment = gradableTypes.includes(type);

  let tooltipText = '';

  if (hasTooltip) {
    if (isGradedAssessment) {
      tooltipText = _t('Graded assessment');
    } else if (isAssessment) {
      tooltipText = _t('Practice or optional assessment');
    } else if (type === 'supplement') {
      tooltipText = _t('Reading');
    } else if (type === 'discussionPrompt') {
      tooltipText = _t('Discussion Prompt');
    } else if (type === 'notebook') {
      tooltipText = _t('Notebook');
    } else if (type === 'lecture') {
      tooltipText = _t('Video');
    } else if (type === 'wiseFlow') {
      tooltipText = 'WiseFlow'; // not translating brandname
    } else if (type === 'dialogue') {
      tooltipText = _t('Dialogue');
    } else if (type === 'rolePlay') {
      tooltipText = _t('Role Play');
    }
  }

  return (
    <TooltipWrapper placement={tooltipPlacement ?? 'top'} message={tooltipText}>
      {renderIcon(!!tooltipText)}
    </TooltipWrapper>
  );
};

export default ItemIcon;
